import { useState, useEffect, useRef, useCallback } from "react";
import { Maximize2, Minimize2, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import videoMobile from "@/assets/home-video-mobile.mp4.asset.json";
import videoDesktop from "@/assets/home-video-desktop.mp4.asset.json";
import videoPoster from "@/assets/home-video-poster.jpg.asset.json";

const VideoScrollSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [scrollScale, setScrollScale] = useState(0.45);
  const [showExpandIcon, setShowExpandIcon] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const containerHeight = containerRef.current.offsetHeight;
      const vh = window.innerHeight;

      const scrolled = Math.max(0, -rect.top);
      const maxScroll = containerHeight - vh;
      const progress = Math.min(scrolled / maxScroll, 1);

      setScrollScale(0.6 + progress * 0.4);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Show expand icon when video is at full scale
  useEffect(() => {
    setShowExpandIcon(scrollScale >= 0.98);
  }, [scrollScale]);

  // Handle fullscreen changes (including iOS-specific events)
  useEffect(() => {
    const video = videoRef.current;

    const handleFullscreenChange = () => {
      const isFs = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (video as any)?.webkitDisplayingFullscreen
      );
      setIsFullscreen(isFs);

      if (video && !isFs) {
        // Exiting fullscreen - mute and loop
        video.muted = true;
        video.loop = true;
        setIsMuted(true);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    // iOS-specific fullscreen events on video element
    if (video) {
      video.addEventListener("webkitbeginfullscreen", handleFullscreenChange);
      video.addEventListener("webkitendfullscreen", handleFullscreenChange);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      if (video) {
        video.removeEventListener("webkitbeginfullscreen", handleFullscreenChange);
        video.removeEventListener("webkitendfullscreen", handleFullscreenChange);
      }
    };
  }, []);

  // Sync video time and duration
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('durationchange', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handlePause);

    // If metadata already loaded, get duration
    if (video.duration) {
      setDuration(video.duration);
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('durationchange', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handlePause);
    };
  }, []);

  const handleExpandClick = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video) return;

    try {
      // Prepare video for playback
      video.muted = false;
      setIsMuted(false);
      video.loop = false;
      video.currentTime = 0;
      await video.play();

      // iOS Safari: use webkitEnterFullscreen directly on video element
      if ((video as any).webkitEnterFullscreen) {
        (video as any).webkitEnterFullscreen();
      }
      // Android/Desktop: use requestFullscreen on container
      else if (container?.requestFullscreen) {
        await container.requestFullscreen();
        // Try to lock orientation to landscape on Android
        const orientation = screen.orientation as any;
        if (orientation?.lock) {
          try {
            await orientation.lock("landscape");
          } catch {
            // Silently fail if not supported
          }
        }
      } else if ((container as any)?.webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen failed:", error);
    }
  }, []);

  const handleExitFullscreen = useCallback(async () => {
    try {
      const video = videoRef.current;

      // iOS: use webkitExitFullscreen on video
      if ((video as any)?.webkitExitFullscreen) {
        (video as any).webkitExitFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      }

      // Unlock orientation
      const orientation = screen.orientation as any;
      if (orientation?.unlock) {
        orientation.unlock();
      }
    } catch (error) {
      console.error("Exit fullscreen failed:", error);
    }
  }, []);

  const handleSeek = useCallback((value: number[]) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value[0];
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const newMuted = !videoRef.current.muted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  }, []);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <section ref={containerRef} className="relative z-10 h-[150vh] bg-sand-50">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        <div
          ref={videoContainerRef}
          className={cn(
            "relative will-change-transform transition-transform duration-100 ease-out",
            isFullscreen && "w-full h-full flex items-center justify-center bg-black",
          )}
          style={!isFullscreen ? { transform: `scale(${scrollScale})` } : undefined}
        >
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster={videoPoster.url}
            className={cn(
              "object-cover rounded-2xl shadow-lift aspect-video",
              isFullscreen ? "w-full h-full rounded-none" : "w-[90vw] max-w-6xl h-auto",
            )}
          >
            <source src={videoMobile.url} type="video/mp4" media="(max-width: 767px)" />
            <source src={videoDesktop.url} type="video/mp4" />
          </video>

          {/* Controls - appears when video is full size */}
          {showExpandIcon && !isFullscreen && (
            <div className="absolute bottom-4 right-4 flex items-center gap-2 animate-fade-in">
              {/* Volume Toggle */}
              <button
                onClick={toggleMute}
                className="bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-all duration-300 backdrop-blur-sm"
                aria-label={isMuted ? "Activar sonido" : "Silenciar"}
              >
                {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </button>

              {/* Expand to Fullscreen */}
              <button
                onClick={handleExpandClick}
                className="bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-all duration-300 backdrop-blur-sm"
                aria-label="Expandir a pantalla completa"
              >
                <Maximize2 className="w-6 h-6" />
              </button>
            </div>
          )}

          {/* Fullscreen Controls */}
          {isFullscreen && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 animate-fade-in">
              <div className="flex items-center gap-4 max-w-4xl mx-auto">
                {/* Play/Pause */}
                <button
                  onClick={togglePlayPause}
                  className="text-white hover:text-sand-200 transition-colors"
                  aria-label={isPlaying ? "Pausar" : "Reproducir"}
                >
                  {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
                </button>

                {/* Volume Toggle */}
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-sand-200 transition-colors"
                  aria-label={isMuted ? "Activar sonido" : "Silenciar"}
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>

                {/* Time */}
                <span className="text-white text-sm font-medium min-w-[45px]">{formatTime(currentTime)}</span>

                {/* Progress Bar */}
                <div className="flex-1">
                  <Slider
                    value={[currentTime]}
                    max={duration || 100}
                    step={0.1}
                    onValueChange={handleSeek}
                    className="cursor-pointer"
                  />
                </div>

                {/* Duration */}
                <span className="text-white text-sm font-medium min-w-[45px]">{formatTime(duration)}</span>

                {/* Exit Fullscreen */}
                <button
                  onClick={handleExitFullscreen}
                  className="text-white hover:text-sand-200 transition-colors"
                  aria-label="Salir de pantalla completa"
                >
                  <Minimize2 className="w-6 h-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default VideoScrollSection;
